



import React, { useState, useContext, createContext, useEffect, useRef, useId } from 'react';
import {
  UserCircle, Lock, Buildings, UsersThree, Stack, ClipboardText,
  ArrowsLeftRight, ShieldCheck, Gear, Eye, SignOut, List, X,
  House, Clock, WarningCircle, CheckCircle, ChartBar,
  PlayCircle, Bell, MagnifyingGlass, CaretRight, ArrowLeft,
  CurrencyCircleDollar
} from '@phosphor-icons/react/ssr';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import defaultLogo from './assets/images/logo10s.png';
import AdminCompaniesModule from './AdminCompaniesModule';
import SuperAdminModule from './SuperAdminModule';
import ProfileModule from './ProfileModule';
import BrandingModule from './BrandingModule';
import UsersModule from './UsersModule';
import RegisterPage from './RegisterPage';
import TemplateManagementModule from './TemplateManagementModule';
import ValidationRulesModule from './ValidationRulesModule';
import ValidationExecutionModule from './ValidationExecutionModule';
import ReportComparisonModule from './ReportComparisonModule';
import AuditModule from './AuditModule';
import BillingModule from './BillingModule';
import AcceptInvitePage from './AcceptInvitePage';
import BillingSuccessPage from './BillingSuccessPage';
import { api, API_BASE_URL } from './services/apiClient';
import { logger } from './services/logger';

const GOOGLE_CLIENT_ID = '928459025377-vbq93ao4uhk6f5dv6toq8pvb6rk8cgi2.apps.googleusercontent.com';

// ============ CONTEXTS ============
const AuthContext = createContext();
const ThemeContext = createContext();
const PermissionsContext = createContext();
const NotificationContext = createContext();
const LoadingContext = createContext();
const ExecutionContext = createContext();

// ============ HOOKS ============
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) throw new Error('usePermissions must be used within PermissionsProvider');
  return context;
};

const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within NotificationProvider');
  return context;
};

const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) throw new Error('useLoading must be used within LoadingProvider');
  return context;
};

const useExecution = () => {
  const context = useContext(ExecutionContext);
  if (!context) throw new Error('useExecution must be used within ExecutionProvider');
  return context;
};

// ============ FUNCIONES UTILITY ============

/**
 * Calcula la luminosidad percibida de un color hex
 * Retorna true si es claro (usar texto oscuro), false si es oscuro (usar texto claro)
 */
const isColorLight = (hexColor) => {
  try {
    // Convertir hex a RGB
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Fórmula de luminosidad relativa WCAG
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Si luminosidad > 0.5, es claro; sino, es oscuro
    return luminance > 0.5;
  } catch {
    // Fallback: asumir oscuro
    return false;
  }
};

// ============ COMPONENTES BASE ============
const BaseButton = ({
  children,
  variant = 'primary',
  size = 'md',
  onClick,
  disabled,
  icon: Icon,
  type = 'button',
  className = ''
}) => {
  const variants = {
    primary:   'bg-primary hover:bg-primary-hover active:bg-primary-dark text-white shadow-sm hover:shadow',
    secondary: 'bg-secondary hover:bg-secondary-hover active:bg-secondary-dark text-white shadow-sm hover:shadow',
    outline:   'border border-[var(--border-strong)] text-[var(--text)] hover:bg-[var(--surface-raised)] dark:border-[var(--border)] dark:hover:bg-[var(--surface-raised)]',
    ghost:     'text-[var(--text-secondary)] hover:bg-[var(--surface-raised)] hover:text-[var(--text)]',
    danger:    'bg-error hover:bg-error-text text-white shadow-sm hover:shadow',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-xs gap-1.5',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-5 py-2.5 text-base gap-2',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex items-center justify-center font-medium rounded-lg
        transition-all duration-150 select-none
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]} ${sizes[size]}
        ${className}
      `}
    >
      {Icon && <Icon size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />}
      {children}
    </button>
  );
};

const BaseInput = ({
  label,
  id: propId,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  error,
  onClearError,
  icon: Icon,
  className = '',
  ...rest
}) => {
  // Genera un id único por instancia para vincular label↔input (accesibilidad + Playwright getByLabel)
  const autoId = useId();
  const inputId = propId ?? (label ? autoId : undefined);

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-[var(--text)]">
          {label} {required && <span className="text-error">*</span>}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={16} />
        )}
        <input
          id={inputId}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error && inputId ? `${inputId}-error` : undefined}
          className={`
            w-full py-2.5 border rounded-lg text-sm
            bg-[var(--surface)] text-[var(--text)]
            placeholder:text-[var(--text-muted)]
            border-[var(--border)]
            focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary
            transition-colors duration-150
            ${Icon ? 'pl-9 pr-4' : 'px-4'}
            ${error ? 'border-error focus:ring-error/30 focus:border-error' : ''}
          `}
          {...rest}
        />
      </div>
      {error && (
        <p id={inputId ? `${inputId}-error` : undefined} className="text-xs text-error mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

const BaseCard = ({ children, title, className = '', onClick }) => {
  return (
    <div
      className={`
        bg-[var(--surface)] rounded-xl border border-[var(--border)]
        shadow-card transition-shadow duration-200
        ${onClick ? 'cursor-pointer hover:shadow-card-md' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {title && (
        <div className="px-6 py-4 border-b border-[var(--border)]">
          <h3 className="text-base font-semibold text-[var(--text)]">{title}</h3>
        </div>
      )}
      <div className={`p-6 ${onClick ? 'pointer-events-none' : ''}`}>{children}</div>
    </div>
  );
};

const LoadingSpinner = ({ size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-8 h-8' };
  return (
    <div className={`${sizes[size]} animate-spin rounded-full border-2 border-[var(--border)] border-t-primary`} />
  );
};

// ============ NOTIFICATION COMPONENT ============
const NotificationContainer = () => {
  const { notifications, removeNotification } = useNotification();
  const visible = notifications.slice(-5);

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {visible.map(notification => (
        <div
          key={notification.id}
          className={`
            px-4 py-3 rounded-lg shadow-lg max-w-sm
            ${notification.type === 'success' ? 'bg-success text-white' : ''}
            ${notification.type === 'error'   ? 'bg-error text-white' : ''}
            ${notification.type === 'info'    ? 'bg-secondary text-white' : ''}
            ${notification.type === 'warning' ? 'bg-warning text-white' : ''}
          `}
        >
          <div className="flex justify-between items-start">
            <p className="text-sm font-medium">{notification.message}</p>
            <button
              onClick={() => removeNotification(notification.id)}
              className="ml-2 text-white hover:text-gray-200"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ LOADING OVERLAY COMPONENT ============
const LoadingOverlay = () => {
  const { isLoading, loadingMessage } = useLoading();

  if (!isLoading) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="flex flex-col items-center space-y-4">
          <LoadingSpinner size="lg" />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Procesando...
            </h3>
            <p className="text-sm text-gray-600">
              {loadingMessage || 'Por favor espere mientras se completa la operación.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ CHANGE PASSWORD (obligatorio tras reset del superadmin) ============
const ChangePasswordPage = () => {
  const { user, updateUser } = useAuth();
  const { addNotification } = useNotification();
  const [form, setForm] = useState({ newPassword: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (form.newPassword !== form.confirm) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true);
    try {
      const { api } = await import('./services/apiClient');
      await api.patch('/api/profile/password', { newPassword: form.newPassword });
      updateUser({ mustChangePassword: false });
      addNotification('Contraseña actualizada correctamente', 'success');
    } catch {
      setError('Error al cambiar la contraseña. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div className="w-full max-w-sm">
        <div className="rounded-2xl p-8 shadow-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">🔐</div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--text)' }}>Cambia tu contraseña</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Hola <strong>{user?.username}</strong>, el administrador generó una contraseña temporal. Debes cambiarla para continuar.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <BaseInput
              label="Nueva contraseña"
              type="password"
              value={form.newPassword}
              onChange={e => { setForm(p => ({ ...p, newPassword: e.target.value })); setError(''); }}
              placeholder="Mínimo 8 caracteres"
            />
            <BaseInput
              label="Confirmar contraseña"
              type="password"
              value={form.confirm}
              onChange={e => { setForm(p => ({ ...p, confirm: e.target.value })); setError(''); }}
              placeholder="Repite la contraseña"
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
            <BaseButton type="submit" className="w-full mt-2" disabled={loading} size="lg">
              {loading ? 'Guardando…' : 'Cambiar contraseña y continuar'}
            </BaseButton>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============ SSO PAGE — Deep Linking desde el Add-in (TDS §2.2) ============
const SsoPage = () => {
  const { login } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ott  = params.get('token');
    const dest = params.get('dest') || 'dashboard';

    if (!ott) {
      setError('Enlace de acceso inválido o incompleto.');
      return;
    }

    (async () => {
      try {
        const res  = await fetch(`${API_BASE_URL()}/api/auth/exchange-ott`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ ott })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Error al iniciar sesión.');

        localStorage.setItem('authToken', data.token);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);

        login({
          username:           data.user.username,
          fullName:           data.user.fullName,
          role:               data.user.role,
          company:            data.user.company,
          tenantId:           data.user.tenantId,
          isProfileComplete:  data.user.isProfileComplete  ?? true,
          mustChangePassword: data.user.mustChangePassword ?? false,
          permissions:        data.user.permissions        ?? []
        });

        // Mapear nombre de dest del addin al nombre de vista del frontend
        const viewMap = { rules: 'validations' };
        const parts = dest.split('/');
        const viewName = viewMap[parts[0]] || parts[0];
        const destId = parts[1] || '';
        const urlParams = new URLSearchParams({ view: viewName });
        if (destId) urlParams.set('ruleId', destId);
        // Reload completo para que React reinicialice con el usuario ya en localStorage
        window.location.href = `/?${urlParams.toString()}`;
      } catch (err) {
        setError(err.message);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const containerStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '100vh',
    fontFamily: 'system-ui, sans-serif', gap: 16
  };

  if (error) {
    return (
      <div style={containerStyle}>
        <p style={{ color: '#ef4444', maxWidth: 400, textAlign: 'center' }}>{error}</p>
        <a href="/" style={{ color: '#6366f1', textDecoration: 'none', fontSize: 14 }}>
          Ir al inicio de sesión
        </a>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <p style={{ color: '#6b7280', fontSize: 15 }}>Iniciando sesión desde el Add-in…</p>
    </div>
  );
};

// ============ LOGIN COMPONENT ============
const LoginPage = ({ onNavigate }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { addNotification } = useNotification();
  const { theme } = useTheme();

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL()}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: credentialResponse.credential })
      });
      const data = await response.json();

      if (response.status === 404 && data.canRegister) {
        // Usuario no registrado — guardar datos de Google para pre-llenar el registro
        sessionStorage.setItem('googleRegData', JSON.stringify({
          email:    data.email,
          name:     data.name,
          googleId: data.googleId,
          idToken:  credentialResponse.credential
        }));
        onNavigate?.('register');
        return;
      }

      if (!response.ok) throw new Error(data.message || 'Error del servidor');

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      login({
        username:           data.user.username,
        fullName:           data.user.fullName,
        role:               data.user.role,
        company:            data.user.company,
        tenantId:           data.user.tenantId,
        isProfileComplete:  data.user.isProfileComplete ?? true,
        mustChangePassword: data.user.mustChangePassword ?? false,
        permissions:        data.user.permissions
      });
      addNotification('Inicio de sesión con Google exitoso', 'success');
    } catch (err) {
      const isNetworkError = err instanceof TypeError || err.message?.toLowerCase().includes('fetch') || err.message?.toLowerCase().includes('network');
      setError(isNetworkError
        ? 'Servidor no disponible. Intente más tarde.'
        : err.message || 'Error al iniciar sesión con Google');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(''); // Limpiar error solo al enviar

    try {
      const response = await fetch(`${API_BASE_URL()}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          applicationCode: '7ae35eaa-5fec-4ba3-9b74-48b0c6355995'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error del servidor');
      }

      if (!data.token) {
        throw new Error('Respuesta inválida del servidor');
      }

      // Guardar tokens JWT
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);

      // Login con datos del usuario
      login({
        username:           data.user.username,
        fullName:           data.user.fullName,
        role:               data.user.role,
        company:            data.user.company,
        tenantId:           data.user.tenantId,
        isProfileComplete:  data.user.isProfileComplete ?? true,
        mustChangePassword: data.user.mustChangePassword ?? false,
        permissions:        data.user.permissions
      });

      logger.info('Login exitoso', {
        component: 'LoginPage',
        username: credentials.username
      });

      addNotification('Inicio de sesión exitoso', 'success');
    } catch (err) {
      logger.error('Error en login', {
        component: 'LoginPage',
        username: credentials.username,
        error: err.message
      });
      const isNetworkError = err instanceof TypeError || err.message?.toLowerCase().includes('fetch') || err.message?.toLowerCase().includes('network');
      setError(isNetworkError
        ? 'Servidor no disponible. Intente más tarde.'
        : err.message || 'Error en el servidor. Verifique la conexión.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ background: 'var(--bg)' }}
    >
      {/* Panel izquierdo decorativo — oculto en móvil */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center p-12"
        style={{ background: 'var(--secondary-dark, #2C3966)' }}
      >
        <div style={{ background: 'white', borderRadius: '12px', padding: '10px 20px', marginBottom: '2rem' }}>
          <img
            src={theme.logo || defaultLogo}
            alt="Logo"
            className="w-40 h-auto object-contain"
            onError={e => { e.currentTarget.src = defaultLogo; }}
          />
        </div>
        <h2 className="text-3xl font-bold text-white text-center leading-tight">
          GeoCheck
        </h2>
        <p className="text-white/60 text-center mt-3 max-w-xs text-sm leading-relaxed">
          Plataforma de validación de GeoDatabases conforme a la Resolución 2182 de 2016 — IGAC
        </p>
        <div className="mt-10 flex gap-6">
          {[['Plantillas', 'Configura reglas'], ['Validaciones', 'Ejecuta análisis'], ['Informes', 'Genera reportes']].map(([t, d]) => (
            <div key={t} className="text-center">
              <p className="text-white font-semibold text-sm">{t}</p>
              <p className="text-white/50 text-xs mt-0.5">{d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {/* Logo en móvil */}
        <div className="lg:hidden mb-8 text-center">
          <div style={{ background: 'white', borderRadius: '10px', padding: '8px 16px', display: 'inline-block', marginBottom: '0.5rem' }}>
            <img
              src={theme.logo || defaultLogo}
              alt="Logo"
              className="w-28 h-auto object-contain"
              onError={e => { e.currentTarget.src = defaultLogo; }}
            />
          </div>
          <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'var(--text-muted)' }}>
            GeoCheck
          </p>
        </div>

        <div
          className="w-full max-w-sm rounded-2xl p-8"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <div className="mb-7">
            <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
              Iniciar sesión
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
              {'Acceso a GeoCheck'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <BaseInput
              label="Usuario"
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
              placeholder="Ingresa tu usuario"
              required
              icon={UserCircle}
              error={error}
            />
            <BaseInput
              label="Contraseña"
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
              placeholder="••••••••"
              required
              icon={Lock}
            />
            <BaseButton type="submit" className="w-full mt-2" disabled={loading} size="lg">
              {loading ? <LoadingSpinner size="sm" /> : 'Ingresar'}
            </BaseButton>
          </form>

          {/* Divisor */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>o continúa con</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
          </div>

          {/* Botón de Google */}
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setError('Error al iniciar sesión con Google')}
              text="signin_with"
              locale="es"
              width="100%"
            />
          </div>

          {/* Links de navegación pública */}
          {onNavigate && (
            <div className="mt-4 flex flex-col items-center gap-2">
              <button
                onClick={() => onNavigate('forgot-password')}
                className="text-xs transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={e => e.target.style.color = 'var(--primary)'}
                onMouseLeave={e => e.target.style.color = 'var(--text-tertiary)'}
              >
                ¿Olvidaste tu contraseña?
              </button>
              <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                <span>¿No tienes cuenta?</span>
                <button
                  onClick={() => onNavigate('register')}
                  className="font-semibold transition-colors"
                  style={{ color: 'var(--primary)' }}
                >
                  Crear cuenta
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ SIDEBAR NAVIGATION ============
const Sidebar = ({ isOpen, onClose, currentView, setCurrentView }) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { theme } = useTheme();

  // Calcular color del sidebar oscureciendo el secundario
  // Patrón: 30% del color + 70% negro = oscurecimiento consistente
  // Esto aplica el mismo patrón de secondaryDark en design-tokens
  const sidebarBgColor = `color-mix(in srgb, var(--secondary) 30%, #000)`;

  // El sidebar siempre es oscuro (secondary 30% + black 70%), así que usar texto blanco
  const textColor = '#FFFFFF';

  const menuItems = [
    { icon: House,        label: 'Dashboard',         id: 'dashboard',  permission: null },
    { icon: Stack,        label: 'Plantillas',        id: 'templates',  permission: 'manage_templates' },
    { icon: ClipboardText,label: 'Validaciones',      id: 'validations',permission: 'manage_validations' },
    { icon: PlayCircle,   label: 'Ejecución',         id: 'execute',    permission: 'execute_validations' },
    { icon: ChartBar,     label: 'Informes',          id: 'compare',    permission: 'compare_reports' },
    { icon: ShieldCheck,  label: 'Auditoría',         id: 'audit',      superAdminOnly: true },
    { icon: UsersThree,   label: 'Usuarios',          id: 'users',      superAdminOnly: true },
    { icon: Gear,         label: 'Mi Empresa',        id: 'branding',   superAdminOnly: true },
    { icon: Buildings,    label: 'Panel Superadmin',  id: 'admin',      onlySuperAdmin: true },
    { icon: CurrencyCircleDollar, label: 'Facturación', id: 'billing',    superAdminOnly: true },
    { icon: UserCircle,   label: 'Mi perfil',          id: 'profile',    permission: null },
  ];

  const visibleItems = menuItems.filter(item => {
    if (item.onlySuperAdmin && user?.role !== 'superadmin') return false;
    if (item.superAdminOnly && user?.role !== 'admin' && user?.role !== 'superadmin') return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    return true;
  });

  return (
    <>
      {/* Overlay móvil */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'var(--overlay)' }}
          onClick={onClose}
        />
      )}

      {/* Sidebar — dinámico, usa color secundario oscurecido */}
      <div className={`
        fixed left-0 top-0 h-full w-60 z-50
        flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto
      `}
        style={{ background: sidebarBgColor }}
      >
        {/* Logo */}
        <div className="flex flex-col items-center pt-6 pb-4 px-4"
          style={{ borderBottom: `1px solid ${textColor}${Math.round(0.07 * 255).toString(16).padStart(2, '0')}` }}
        >
          {/* Botón cerrar solo visible en móvil — posicionado absoluto para no romper el centrado */}
          <button
            onClick={onClose}
            className="lg:hidden absolute top-4 right-4 p-1 rounded"
            style={{ color: `${textColor}${Math.round(0.4 * 255).toString(16).padStart(2, '0')}` }}
          >
            <X size={18} />
          </button>

          {/* Contenedor del logo: se ajusta al tamaño natural */}
          <div style={{
            background: 'rgba(255,255,255,0.10)',
            borderRadius: '12px',
            padding: '10px 14px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img
              src={theme.logo || defaultLogo}
              alt="Logo"
              className="h-14 w-auto object-contain"
              style={{ maxWidth: '130px' }}
            />
          </div>

          {/* Subtítulo: centrado y subordinado visualmente al logo */}
          <p className="text-[10px] font-normal mt-2 tracking-wide"
            style={{ color: `${textColor}${Math.round(0.35 * 255).toString(16).padStart(2, '0')}` }}
          >
            powered by <span className="font-semibold">GEOCheck</span>
          </p>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
          <p className="px-3 mb-3 text-[9px] font-bold uppercase tracking-[0.2em]"
            style={{ color: `${textColor}${Math.round(0.25 * 255).toString(16).padStart(2, '0')}` }}
          >
            Navegación
          </p>
          {visibleItems.map((item, index) => {
            const isActive = currentView === item.id;
            return (
              <button
                key={index}
                onClick={() => { setCurrentView(item.id); onClose(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-all duration-150"
                style={{
                  background: isActive ? 'color-mix(in srgb, var(--primary) 18%, transparent)' : 'transparent',
                  color: isActive ? 'var(--primary)' : `${textColor}${Math.round(0.55 * 255).toString(16).padStart(2, '0')}`,
                  fontWeight: isActive ? 600 : 400,
                  borderLeft: isActive ? '3px solid var(--primary)' : '3px solid transparent',
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = `${textColor}${Math.round(0.05 * 255).toString(16).padStart(2, '0')}`;
                    e.currentTarget.style.color = `${textColor}${Math.round(0.85 * 255).toString(16).padStart(2, '0')}`;
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = `${textColor}${Math.round(0.55 * 255).toString(16).padStart(2, '0')}`;
                  }
                }}
              >
                <item.icon size={16} style={{ flexShrink: 0 }} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Usuario — clic lleva a Mi perfil */}
        <div className="px-3 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)' }}
            onClick={() => { setCurrentView('profile'); onClose(); }}
            title="Ver mi perfil"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xs"
              style={{ background: 'var(--primary)' }}
            >
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: 'rgba(255,255,255,0.85)' }}>
                {user?.fullName || user?.username}
              </p>
              <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {user?.company || user?.role}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Íconos de tema (inline SVG para no añadir dependencias) ──────────────────
const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

// ============ HEADER ============
const Header = ({ onMenuClick, currentView }) => {
  const { user, logout } = useAuth();
  const { addNotification } = useNotification();
  const { isDark, toggleDarkMode } = useTheme();

  const handleLogout = () => {
    logout();
    addNotification('Sesión cerrada correctamente', 'info');
  };

  const getViewTitle = (view) => {
    const titles = {
      dashboard:   'Dashboard',
      admin:       'Administración',
      templates:   'Plantillas',
      validations: 'Validaciones',
      execute:     'Ejecución',
      compare:     'Informes',
      audit:       'Auditoría',
    };
    return titles[view] || 'Dashboard';
  };

  return (
    <header
      className="sticky top-0 z-30 flex items-center justify-between px-6 h-14"
      style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {/* Izquierda: hamburger (móvil) + breadcrumb */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--text-secondary)' }}
        >
          <List size={20} />
        </button>
        <h1 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
          {getViewTitle(currentView)}
        </h1>
      </div>

      {/* Derecha: toggle tema + usuario + cerrar sesión */}
      <div className="flex items-center gap-2">
        {/* Toggle dark/light */}
        <button
          onClick={toggleDarkMode}
          title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          className="p-2 rounded-lg transition-colors"
          style={{ color: 'var(--text-secondary)', background: 'transparent' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-raised)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>

        {/* Separador */}
        <div className="w-px h-5 mx-1" style={{ background: 'var(--border)' }} />

        {/* Usuario */}
        <span className="hidden sm:block text-xs" style={{ color: 'var(--text-secondary)' }}>
          {user?.username}
        </span>

        <BaseButton variant="ghost" size="sm" onClick={handleLogout} icon={SignOut}>
          <span className="hidden sm:inline">Salir</span>
        </BaseButton>
      </div>
    </header>
  );
};

// ============ PROFILE COMPLETION BANNER ============
const ProfileCompletionBanner = () => {
  const { user, updateUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // No mostrar si el perfil está completo, si es superadmin, o si fue ocultado temporalmente
  if (!user || user.isProfileComplete || user.role === 'superadmin' || dismissed) return null;

  return (
    <div
      className="flex items-center justify-between gap-3 px-6 py-2 text-sm"
      style={{
        background: 'var(--warning-light)',
        color: 'var(--warning-text)',
        borderBottom: '1px solid #fde68a',
        borderLeft: '4px solid var(--warning)',
        minHeight: '40px',
      }}
    >
      <div className="flex items-center gap-2" style={{ lineHeight: '1.4' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <span className="font-medium">Perfil de empresa incompleto.</span>
        <span className="opacity-80">Completa la información para una mejor experiencia.</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          className="font-semibold underline hover:opacity-70 transition-opacity"
          style={{ color: 'var(--warning-text)' }}
          onClick={() => {
            window.dispatchEvent(new CustomEvent('navigate', { detail: 'profile' }));
          }}
        >
          Completar ahora
        </button>
        <button
          className="flex items-center justify-center hover:opacity-60 transition-opacity"
          style={{ color: 'var(--warning-text)', width: 20, height: 20, lineHeight: 1 }}
          onClick={() => setDismissed(true)}
          title="Ocultar por ahora"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

// ============ FORGOT PASSWORD PAGE ============
const ForgotPasswordPage = ({ onNavigate }) => {
  const { theme } = useTheme();
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)' }}
    >
      <div className="w-full max-w-md">
        {theme?.logo && (
          <div className="flex justify-center mb-6">
            <img src={theme.logo} alt="Logo" className="h-14 object-contain" />
          </div>
        )}
        <div className="rounded-2xl p-8 text-center" style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(12px)' }}>
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-white mb-2">¿Olvidaste tu contraseña?</h2>
          <p className="text-white/70 text-sm mb-4 leading-relaxed">
            La recuperación de contraseña es gestionada por el administrador de tu empresa.
          </p>
          <div
            className="rounded-xl p-4 mb-6 text-left space-y-3"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          >
            <p className="text-white/90 text-sm font-medium">¿Qué hacer?</p>
            <ol className="text-white/60 text-sm space-y-2 list-decimal list-inside">
              <li>Contacta al administrador de tu empresa.</li>
              <li>El administrador solicitará un restablecimiento al equipo de VALIDA.</li>
              <li>Recibirás una contraseña temporal para ingresar y cambiarla.</li>
            </ol>
            <div className="pt-2 border-t border-white/10">
              <p className="text-white/50 text-xs">
                Si eres administrador, escríbenos a{' '}
                <strong className="text-white/70">soporte@10s.co</strong>
              </p>
            </div>
          </div>
          <button
            onClick={() => onNavigate('login')}
            className="w-full py-3 rounded-xl font-semibold text-white transition-all"
            style={{ background: 'var(--primary)' }}
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN LAYOUT ============
const MainLayout = ({ children, currentView, setCurrentView }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentView={currentView}
        setCurrentView={setCurrentView}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header
          onMenuClick={() => setSidebarOpen(true)}
          currentView={currentView}
        />
        <ProfileCompletionBanner />
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// ============ DASHBOARD CONTENT ============
const DashboardContent = ({ setCurrentView }) => {
  const { requestViewExecution } = useExecution();
  const { user } = useAuth();

  const [stats, setStats] = useState({ templates: null, validations: null, executions: null, reports: null });
  const [lastExecution, setLastExecution] = useState(undefined);
  const [recentActivity, setRecentActivity] = useState(null);
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < 1) return 'Hace un momento';
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays === 1) return 'Ayer';
    return `Hace ${diffDays} días`;
  };

  const getActivityBadgeStyle = (activity) => {
    const result = (activity.result || activity.status || '').toLowerCase();
    if (result.includes('success') || result.includes('ok') || result.includes('completado'))
      return 'bg-blue-100 text-blue-800';
    if (result.includes('error') || result.includes('fail'))
      return 'bg-red-100 text-red-800';
    if (result.includes('warning'))
      return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getExecutionStatusStyle = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'CompletedWithWarns': return 'bg-yellow-100 text-yellow-800';
      case 'failed': case 'Failed': return 'bg-red-100 text-red-800';
      case 'running': case 'Running': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getExecutionStatusLabel = (status) => {
    switch (status) {
      case 'Completed': return 'Completado';
      case 'CompletedWithWarns': return 'Con Advertencias';
      case 'failed': case 'Failed': return 'Error';
      case 'running': case 'Running': return 'En Progreso';
      default: return status || 'Desconocido';
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    const reportsUrl = user?.role === 'admin'
      ? '/api/reports/generated?all=true'
      : '/api/reports/generated';

    const [templatesRes, validationsRes, executionsRes, recentRes, activityRes, reportsRes] =
      await Promise.allSettled([
        api.get('/api/templates'),
        api.get('/api/validation'),
        api.get('/api/validationexecutions/stats'),
        api.get('/api/validationexecutions/recent'),
        api.get('/api/audit/logs?pageSize=5'),
        api.get(reportsUrl)
      ]);

    // Templates — solo las del usuario logueado (admin ve todas)
    if (templatesRes.status === 'fulfilled') {
      const data = templatesRes.value;
      const arr = Array.isArray(data) ? data : (data?.items || data?.data || []);
      const mine = user?.role === 'admin' ? arr : arr.filter(t => t.createdBy === user?.username);
      setStats(prev => ({ ...prev, templates: mine.length }));
      if (arr.some(item => item._isMockData)) setIsUsingMockData(true);
    } else {
      setStats(prev => ({ ...prev, templates: 0 }));
    }

    // Validaciones — solo las del usuario logueado (admin ve todas)
    if (validationsRes.status === 'fulfilled') {
      const data = validationsRes.value;
      const arr = Array.isArray(data) ? data : (data?.items || data?.data || []);
      const mine = user?.role === 'admin' ? arr : arr.filter(v => v.createdBy === user?.username);
      setStats(prev => ({ ...prev, validations: mine.length }));
    } else {
      setStats(prev => ({ ...prev, validations: 0 }));
    }

    // Ejecuciones — viene de /stats
    if (executionsRes.status === 'fulfilled') {
      const data = executionsRes.value;
      const total = data?.total ?? data?.totalExecutions ?? data?.count ?? 0;
      setStats(prev => ({ ...prev, executions: total }));
    } else {
      setStats(prev => ({ ...prev, executions: 0 }));
    }

    // Última ejecución — viene de /recent (array), tomar el primero
    if (recentRes.status === 'fulfilled') {
      const data = recentRes.value;
      const arr = Array.isArray(data) ? data : (data?.items || []);
      setLastExecution(arr.length > 0 ? arr[0] : null);
      if (executionsRes.status !== 'fulfilled') {
        setStats(prev => ({ ...prev, executions: arr.length }));
      }
    } else {
      setLastExecution(null);
    }

    // Actividad reciente — viene de audit/logs (el backend filtra por usuario del token)
    if (activityRes.status === 'fulfilled') {
      const data = activityRes.value;
      const arr = Array.isArray(data) ? data : (data?.items || data?.logs || []);
      setRecentActivity(arr.slice(0, 5));
    } else {
      setRecentActivity([]);
    }

    // Informes
    if (reportsRes.status === 'fulfilled') {
      const data = reportsRes.value;
      const arr = Array.isArray(data) ? data : (data?.items || data?.data || []);
      setStats(prev => ({ ...prev, reports: arr.length }));
    } else {
      setStats(prev => ({ ...prev, reports: 0 }));
    }
  };

  const ACTION_LABELS = {
    'LOGIN':             'Inicio de sesión',
    'LOGIN_FAILED':      'Intento de login fallido',
    'TEMPLATE_UPLOAD':   'Plantilla subida',
    'TEMPLATE_UPDATE':   'Plantilla actualizada',
    'TEMPLATE_DELETE':   'Plantilla eliminada',
    'TEMPLATE_CLONE':    'Plantilla clonada',
    'VALIDATION_CREATE': 'Regla de validación creada',
    'VALIDATION_UPDATE': 'Regla de validación actualizada',
    'VALIDATION_DELETE': 'Regla de validación eliminada',
    'VALIDATION_TOGGLE': 'Regla activada/desactivada',
    'EXECUTION_START':   'Ejecución de validación iniciada',
  };

  const handleViewLastExecution = () => {
    if (lastExecution) {
      requestViewExecution(lastExecution.id ?? lastExecution._raw?.id);
      setCurrentView('execute');
    }
  };

  // Configuración de las métricas del dashboard
  const metricCards = [
    {
      label: 'Plantillas',
      value: stats.templates,
      icon: Stack,
      accent: '#3D4E8C',
      accentBg: 'rgba(61,78,140,0.10)',
      view: 'templates',
      description: 'GDB configuradas',
    },
    {
      label: 'Validaciones',
      value: stats.validations,
      icon: ClipboardText,
      accent: '#8BA63A',
      accentBg: 'rgba(139,166,58,0.10)',
      view: 'validations',
      description: 'Reglas definidas',
    },
    {
      label: 'Ejecuciones',
      value: stats.executions,
      icon: PlayCircle,
      accent: '#4E61A3',
      accentBg: 'rgba(78,97,163,0.10)',
      view: 'execute',
      description: 'Análisis realizados',
    },
    {
      label: 'Informes',
      value: stats.reports,
      icon: ChartBar,
      accent: '#6B8230',
      accentBg: 'rgba(107,130,48,0.10)',
      view: 'compare',
      description: 'Reportes generados',
    },
  ];

  const hora = new Date().getHours();
  const saludo = hora < 12 ? 'Buenos días' : hora < 18 ? 'Buenas tardes' : 'Buenas noches';

  return (
    <div className="space-y-6">

      {/* Aviso mock data */}
      {isUsingMockData && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm"
          style={{ background: 'var(--warning-light)', color: 'var(--warning-text)', border: '1px solid var(--warning)' }}
        >
          <WarningCircle size={16} />
          Mostrando datos de ejemplo — el backend no está disponible
        </div>
      )}

      {/* Saludo y bienvenida */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            {saludo}, {user?.username} 👋
          </h2>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Aquí tienes el resumen de tu actividad en GEOCheck
          </p>
        </div>
        {lastExecution && (
          <button
            onClick={handleViewLastExecution}
            className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ background: 'var(--primary)', color: '#fff' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--primary)'}
          >
            <Clock size={15} />
            Ver última ejecución
          </button>
        )}
      </div>

      {/* Métricas — layout horizontal moderno */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards.map(({ label, value, icon: Icon, accent, accentBg, view, description }) => (
          <button
            key={view}
            onClick={() => setCurrentView(view)}
            className="text-left rounded-xl p-5 transition-all duration-200 group"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="rounded-lg p-2.5" style={{ background: accentBg }}>
                <Icon size={20} style={{ color: accent }} />
              </div>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                style={{ background: accentBg, color: accent }}
              >
                →
              </span>
            </div>
            {value === null ? (
              <div className="h-8 w-16 rounded-lg animate-pulse mb-1" style={{ background: 'var(--border)' }} />
            ) : (
              <p className="text-3xl font-bold mb-1" style={{ color: 'var(--text)' }}>{value}</p>
            )}
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{label}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{description}</p>
          </button>
        ))}
      </div>

      {/* Fila inferior: última ejecución + actividad reciente */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Última ejecución — 1/3 */}
        <div className="rounded-xl p-5"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="rounded-lg p-2" style={{ background: 'color-mix(in srgb, var(--primary) 10%, transparent)' }}>
              <Clock size={16} style={{ color: 'var(--primary)' }} />
            </div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Última Ejecución</h3>
          </div>
          {lastExecution === undefined ? (
            <div className="space-y-2">
              <div className="h-4 w-3/4 rounded animate-pulse" style={{ background: 'var(--border)' }} />
              <div className="h-3 w-1/2 rounded animate-pulse" style={{ background: 'var(--border)' }} />
            </div>
          ) : lastExecution === null ? (
            <div className="text-center py-4">
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Sin ejecuciones aún</p>
              <button onClick={() => setCurrentView('execute')}
                className="text-xs mt-2 font-medium" style={{ color: 'var(--primary)' }}>
                Iniciar primera ejecución →
              </button>
            </div>
          ) : (
            <button onClick={handleViewLastExecution} className="w-full text-left group">
              <p className="text-sm font-medium mb-2" style={{ color: 'var(--text)' }}>
                {lastExecution.name || lastExecution.templateName || 'Sin nombre'}
              </p>
              <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${getExecutionStatusStyle(lastExecution.status)}`}>
                {getExecutionStatusLabel(lastExecution.status)}
              </span>
              <p className="text-xs mt-3 font-medium group-hover:underline" style={{ color: 'var(--primary)' }}>
                Ver detalle →
              </p>
            </button>
          )}
        </div>

        {/* Actividad reciente — 2/3 */}
        <div className="lg:col-span-2 rounded-xl"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
        >
          <div className="flex items-center gap-2 px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="rounded-lg p-2" style={{ background: 'rgba(61,78,140,0.1)' }}>
              <CheckCircle size={16} style={{ color: '#3D4E8C' }} />
            </div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Actividad Reciente</h3>
          </div>
          <div className="divide-y" style={{ '--tw-divide-opacity': 1 }}>
            {recentActivity === null ? (
              [1, 2, 3].map(i => (
                <div key={i} className="flex items-center justify-between px-5 py-3.5">
                  <div className="space-y-1.5">
                    <div className="h-3.5 w-48 rounded animate-pulse" style={{ background: 'var(--border)' }} />
                    <div className="h-3 w-28 rounded animate-pulse" style={{ background: 'var(--border)' }} />
                  </div>
                  <div className="h-5 w-16 rounded-full animate-pulse" style={{ background: 'var(--border)' }} />
                </div>
              ))
            ) : recentActivity.length === 0 ? (
              <p className="text-sm px-5 py-6 text-center" style={{ color: 'var(--text-muted)' }}>
                No hay actividad reciente
              </p>
            ) : (
              recentActivity.map((activity, index) => (
                <div key={activity.id || index} className="flex items-center justify-between px-5 py-3.5">
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                      {activity.details || ACTION_LABELS[activity.action] || activity.action}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {activity.userId ? `${activity.userId} · ` : ''}
                      {activity.timestamp ? formatRelativeTime(activity.timestamp) : 'Fecha desconocida'}
                    </p>
                  </div>
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getActivityBadgeStyle(activity)}`}>
                    {activity.result === 'Success' ? 'Éxito' : (activity.result || activity.status || 'Registro')}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ PLACEHOLDER COMPONENTS ============
const PlaceholderModule = ({ title, description }) => {
  return (
    <BaseCard title={title}>
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <Gear size={48} className="mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Módulo en desarrollo</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </BaseCard>
  );
};

// ============ CONTEXT PROVIDERS ============
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar sesión existente con token JWT
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    logger.info('Usuario autenticado', {
      component: 'AuthProvider',
      username: userData.username,
      role: userData.role
    });
    logger.updateUserContext();
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Actualiza campos del usuario sin cerrar sesión (ej: cuando completa el perfil)
  const updateUser = (partial) => {
    setUser(prev => {
      const updated = { ...prev, ...partial };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  const logout = () => {
    logger.info('Usuario cerró sesión', {
      component: 'AuthProvider',
      username: user?.username
    });
    logger.updateUserContext(); // Limpiar contexto del logger

    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // Limpia cachés de branding para que el próximo usuario no vea marca del anterior
    Object.keys(localStorage)
      .filter(k => k.startsWith('valida_branding_'))
      .forEach(k => localStorage.removeItem(k));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ThemeProvider = ({ children }) => {
  // Acceder a la autenticación para saber cuándo cambia el usuario
  // Esto hace que el branding se actualice reactivamente sin refresh
  const { user } = useAuth();

  // Lee el slug del tenant desde la URL: ?tenant=slug (ej: ?tenant=celsia)
  const tenantSlug = new URLSearchParams(window.location.search).get('tenant') || 'default';
  const BRANDING_CACHE_KEY = `valida_branding_${tenantSlug}`;

  const [theme, setTheme] = useState(() => {
    // IMPORTANTE: El useState NUNCA retorna caché del localStorage como estado.
    // El caché puede estar "stale" (viejo) de un usuario anterior.
    //
    // Sí aplicamos CSS variables del caché sincrónicamente para evitar FOUC,
    // pero el estado real lo determina el useEffect haciendo GET al servidor.
    // Así garantizamos que el branding sea el correcto aunque haya caché viejo.

    try {
      const cached = localStorage.getItem(BRANDING_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        const root = document.documentElement;
        // Aplica CSS variables sincrónicamente (para evitar FOUC)
        if (data.primary)   root.style.setProperty('--primary', data.primary);
        if (data.accent)    root.style.setProperty('--primary-hover', data.accent);
        if (data.secondary) root.style.setProperty('--secondary', data.secondary);
        // ⚠️ Pero NO retorna el caché como estado
      }
    } catch { /* localStorage no disponible o datos corruptos */ }

    // Retorna siempre valores por defecto. El useEffect actualizará con datos correctos del servidor.
    return {
      primary:     '#8BA63A',
      secondary:   '#3D4E8C',
      accent:      '#4E61A3',
      logo:        defaultLogo,
      companyName: ''
    };
  });

  // Persiste la preferencia dark mode del área de contenido en localStorage
  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem('valida_dark_mode') === 'true';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      // Aplicar variables de tema oscuro
      root.style.setProperty('--bg', '#1A1D2E');
      root.style.setProperty('--surface', '#252838');
      root.style.setProperty('--text', '#E2E8F0');
      root.style.setProperty('--text-secondary', '#A0AEC0');
      root.style.setProperty('--text-muted', '#718096');
      root.style.setProperty('--border', '#374151');
    } else {
      root.classList.remove('dark');
      // Restaurar variables de tema claro
      root.style.setProperty('--bg', '#F8F9FB');
      root.style.setProperty('--surface', '#FFFFFF');
      root.style.setProperty('--text', '#1E293B');
      root.style.setProperty('--text-secondary', '#64748B');
      root.style.setProperty('--text-muted', '#94A3B8');
      root.style.setProperty('--border', '#E2E8F0');
    }
    localStorage.setItem('valida_dark_mode', isDark);
  }, [isDark]);

  // Carga el branding del tenant al arrancar la app Y cada vez que cambia la autenticación
  // Si está autenticado: usa el JWT para obtener su branding (por tenantId)
  // Si no está autenticado: obtiene el branding público (por slug, default=Tenant 1)
  //
  // El useEffect se re-ejecuta cuando 'user' cambia (login/logout), por lo que el branding
  // se actualiza REACTIVAMENTE sin necesidad de refresh
  useEffect(() => {
    const baseUrl = window._appConfig?.api?.baseUrl ?? 'http://localhost:5128';

    // Usar api.get() en lugar de fetch() para que incluya el JWT si existe
    // Esto permite que el backend lea el tenantId del claim y devuelva el branding correcto
    api.get(`/api/tenants/branding?tenant=${encodeURIComponent(tenantSlug)}`)
      .then(data => {
        if (!data) return;
        const root = document.documentElement;
        if (data.primaryColor)   root.style.setProperty('--primary', data.primaryColor);
        if (data.accentColor)    root.style.setProperty('--primary-hover', data.accentColor);
        if (data.secondaryColor) root.style.setProperty('--secondary', data.secondaryColor);
        setTheme(prev => {
          const updated = {
            ...prev,
            primary:     data.primaryColor   || prev.primary,
            secondary:   data.secondaryColor || prev.secondary,
            accent:      data.accentColor    || prev.accent,
            companyName: data.companyName    || prev.companyName,
            logo:        data.logoUrl ? `${baseUrl}${data.logoUrl}` : prev.logo
          };
          // Actualiza el caché para que la próxima carga sea inmediata (sin FOUC)
          try { localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(updated)); } catch { /* sin espacio */ }
          return updated;
        });
      })
      .catch(() => { /* sin branding configurado — usa valores por defecto */ });
  }, [tenantSlug, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDarkMode = () => setIsDark(prev => !prev);

  // La sidebar siempre usa paleta oscura — es la identidad visual fija de 10S

  const updateTheme = (newTheme) => {
    setTheme(prev => {
      const updated = { ...prev, ...newTheme };
      // Sincroniza el caché cuando el admin guarda cambios desde BrandingModule
      try { localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(updated)); } catch { /* sin espacio */ }
      return updated;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, isDark, toggleDarkMode, tenantSlug }}>
      {children}
    </ThemeContext.Provider>
  );
};

const PermissionsProvider = ({ children }) => {
  const { user } = useAuth() || {};

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    if (user.role === 'admin') return true;
    if (user.role === 'user')  return true;  // rol operativo — acceso completo excepto superAdminOnly
    return user.permissions?.includes(permission) || false;
  };

  return (
    <PermissionsContext.Provider value={{ hasPermission }}>
      {children}
    </PermissionsContext.Provider>
  );
};

const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      removeNotification
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const showLoading = (message = 'Procesando...') => {
    setLoadingMessage(message);
    setIsLoading(true);
  };

  const hideLoading = () => {
    setIsLoading(false);
    setLoadingMessage('');
  };

  return (
    <LoadingContext.Provider value={{
      isLoading,
      loadingMessage,
      showLoading,
      hideLoading
    }}>
      {children}
    </LoadingContext.Provider>
  );
};

// ============ EXECUTION TRACKING ============
const EXECUTION_STORAGE_KEY = 'valida_active_executions';

const isTerminalStatus = (status) =>
  ['Completed', 'CompletedWithWarns', 'CompletedWithErrors',
   'Failed', 'Cancelled', 'Canceled',
   'completed', 'completedwithwarns', 'completedwitherrors',
   'failed', 'cancelled', 'canceled', 'error', 'Error'
  ].includes(status);

const ExecutionProvider = ({ children }) => {
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const [activeExecutions, setActiveExecutions] = useState([]);
  const [pendingViewId, setPendingViewId] = useState(null);
  const pollingRef = useRef(null);
  const executionsRef = useRef([]);
  const dismissTimeoutsRef = useRef({});

  // Mantiene el ref sincronizado con el estado para usarlo en el interval
  const updateExecutions = (updater) => {
    setActiveExecutions(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      executionsRef.current = next;
      return next;
    });
  };

  const saveToStorage = (executions) => {
    if (!user) return;
    const active = executions.filter(e => !isTerminalStatus(e.status));
    if (active.length > 0) {
      localStorage.setItem(EXECUTION_STORAGE_KEY, JSON.stringify(
        active.map(e => ({ executionId: e.id, templateName: e.templateName, username: user.username }))
      ));
    } else {
      localStorage.removeItem(EXECUTION_STORAGE_KEY);
    }
  };

  const stopPolling = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  };

  const clearAllDismissTimeouts = () => {
    Object.values(dismissTimeoutsRef.current).forEach(t => clearTimeout(t));
    dismissTimeoutsRef.current = {};
  };

  useEffect(() => {
    stopPolling();
    updateExecutions([]);
    clearAllDismissTimeouts();
    if (!user) return;

    const saved = localStorage.getItem(EXECUTION_STORAGE_KEY);
    if (saved) {
      try {
        const items = JSON.parse(saved);
        const mine = (Array.isArray(items) ? items : [items])
          .filter(i => i.username === user.username);
        if (mine.length > 0) resumeAllTracking(mine);
      } catch {
        localStorage.removeItem(EXECUTION_STORAGE_KEY);
      }
    }
    return () => { stopPolling(); clearAllDismissTimeouts(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  const scheduleAutoDismiss = (id) => {
    if (dismissTimeoutsRef.current[id]) return;
    dismissTimeoutsRef.current[id] = setTimeout(() => {
      delete dismissTimeoutsRef.current[id];
      updateExecutions(prev => {
        const next = prev.filter(e => e.id !== id);
        saveToStorage(next);
        return next;
      });
    }, 6000);
  };

  const handleNewlyTerminal = (exec) => {
    const s = (exec.status || '').toLowerCase();
    const isOk = s === 'completed';
    const isWarn = s === 'completedwithwarns';
    addNotification(
      isOk   ? `Validación "${exec.templateName}" completada exitosamente` :
      isWarn ? `Validación "${exec.templateName}" completada con advertencias` :
               `La validación "${exec.templateName}" finalizó con errores`,
      isOk ? 'success' : isWarn ? 'warning' : 'error'
    );
    window.dispatchEvent(new CustomEvent('valida:executionCompleted', {
      detail: { id: exec.id, status: exec.status }
    }));
    scheduleAutoDismiss(exec.id);
  };

  const ensurePolling = () => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      const toPoll = executionsRef.current.filter(e => !isTerminalStatus(e.status));
      if (toPoll.length === 0) { stopPolling(); return; }

      const results = await Promise.all(toPoll.map(async e => {
        try {
          const data = await api.get(`/api/validationexecutions/${e.id}/summary`);
          return { id: e.id, data };
        } catch { return null; }
      }));

      updateExecutions(prev => {
        let changed = false;
        const next = prev.map(e => {
          const r = results.find(u => u && u.id === e.id);
          if (!r) return e;
          const updated = {
            ...e,
            status: r.data.status,
            progressPercentage: r.data.progressPercentage || 0,
            executedRules: r.data.executedRules || 0,
            totalRules: r.data.totalRules || 0,
          };
          if (!isTerminalStatus(e.status) && isTerminalStatus(updated.status)) {
            changed = true;
            handleNewlyTerminal(updated);
          }
          return updated;
        });
        if (changed) saveToStorage(next);
        return next;
      });
    }, 4000);
  };

  const startTracking = (executionId, templateName) => {
    if (!user) return;
    updateExecutions(prev => {
      if (prev.some(e => e.id === executionId)) return prev;
      const next = [...prev, { id: executionId, templateName, status: 'Queued', progressPercentage: 0, executedRules: 0, totalRules: 0 }];
      saveToStorage(next);
      return next;
    });
    ensurePolling();
  };

  const resumeAllTracking = async (items) => {
    const results = await Promise.all(items.map(async item => {
      try {
        const data = await api.get(`/api/validationexecutions/${item.executionId}/summary`);
        return { id: item.executionId, templateName: data.templateName || item.templateName, status: data.status,
          progressPercentage: data.progressPercentage || 0, executedRules: data.executedRules || 0, totalRules: data.totalRules || 0 };
      } catch { return null; }
    }));
    const valid = results.filter(Boolean).filter(e => !isTerminalStatus(e.status));
    if (valid.length > 0) {
      updateExecutions(valid);
      ensurePolling();
    } else {
      localStorage.removeItem(EXECUTION_STORAGE_KEY);
    }
  };

  const clearExecution = (id) => {
    if (dismissTimeoutsRef.current[id]) {
      clearTimeout(dismissTimeoutsRef.current[id]);
      delete dismissTimeoutsRef.current[id];
    }
    updateExecutions(prev => {
      const next = prev.filter(e => e.id !== id);
      saveToStorage(next);
      if (next.filter(e => !isTerminalStatus(e.status)).length === 0) stopPolling();
      return next;
    });
  };

  const clearAllTerminal = () => {
    updateExecutions(prev => {
      const next = prev.filter(e => !isTerminalStatus(e.status));
      saveToStorage(next);
      return next;
    });
  };

  const cancelExecution = async (id) => {
    // Optimistic update — mark as Cancelled immediately in UI
    updateExecutions(prev => {
      const next = prev.map(e => e.id === id ? { ...e, status: 'Cancelled' } : e);
      saveToStorage(next);
      return next;
    });
    scheduleAutoDismiss(id);
    try {
      await api.post(`/api/validation/cancel/${id}`);
    } catch {
      // Keep optimistic cancelled state regardless of backend response
    }
  };

  const requestViewExecution = (id) => setPendingViewId(id);
  const clearPendingView = () => setPendingViewId(null);

  return (
    <ExecutionContext.Provider value={{ activeExecutions, startTracking, clearExecution, clearAllTerminal, cancelExecution, pendingViewId, requestViewExecution, clearPendingView }}>
      {children}
    </ExecutionContext.Provider>
  );
};

// Fila individual de una ejecución en el widget
const ExecutionRow = ({ exec, onDismiss, onCancel, onView }) => {
  const s = (exec.status || '').toLowerCase();
  const isQueued = s === 'queued' || s === 'pending';
  const isRunning = !isTerminalStatus(exec.status);
  const isActive = isRunning;
  const isSuccess = s === 'completed' || s === 'completedwithwarns' || s === 'completedwitherrors';
  const isError = !isActive && !isSuccess;
  const pct = exec.progressPercentage || 0;

  const barColor = isSuccess ? 'var(--success)' : isError ? 'var(--error)' : 'var(--primary)';
  const statusLabel = isQueued ? 'En cola' : isRunning ? 'Procesando' : isSuccess ? 'Completado' : 'Error';
  const statusColor = isQueued ? 'var(--text-muted)' : isRunning ? 'var(--primary-text)' : isSuccess ? 'var(--success-text)' : 'var(--error)';

  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
      {/* Fila status + acción */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isQueued
            ? <Clock size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            : isRunning
              ? <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid var(--primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
              : isSuccess
                ? <CheckCircle size={13} style={{ color: 'var(--success)', flexShrink: 0 }} />
                : <WarningCircle size={13} style={{ color: 'var(--error)', flexShrink: 0 }} />
          }
          <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: statusColor }}>
            {statusLabel}
          </span>
        </div>
        {isActive ? (
          <button onClick={() => onCancel(exec.id)}
            style={{ fontSize: '0.7rem', color: 'var(--error)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            Cancelar
          </button>
        ) : (
          <button onClick={() => onDismiss(exec.id)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
            <X size={13} />
          </button>
        )}
      </div>

      {/* Nombre plantilla */}
      <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 6 }} title={exec.templateName}>
        {exec.templateName}
      </p>

      {/* Progreso */}
      {isQueued ? (
        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>En cola — esperando worker disponible</p>
      ) : (
        <>
          <div style={{ width: '100%', height: 3, background: 'var(--border)', borderRadius: 2, marginBottom: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2, background: barColor, transition: 'width 0.7s ease',
              width: pct > 0 ? `${pct}%` : '100%',
              animation: isRunning && pct === 0 ? 'pulse 1.5s ease-in-out infinite' : 'none'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            <span>{exec.totalRules > 0 ? `${exec.executedRules}/${exec.totalRules} reglas` : 'Iniciando...'}</span>
            <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{pct}%</span>
          </div>
        </>
      )}

      <button onClick={() => onView(exec)}
        style={{ marginTop: 6, fontSize: '0.7rem', color: 'var(--primary-text)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
        Ver detalle →
      </button>
    </div>
  );
};

const GlobalExecutionWidget = ({ setCurrentView }) => {
  const { activeExecutions, clearExecution, clearAllTerminal, cancelExecution, requestViewExecution } = useExecution();
  const [pos, setPos] = useState({ bottom: 32, right: 32 });
  const widgetRef = useRef(null);
  const dragRef = useRef(null);
  const prevCountRef = useRef(0);

  // Resetear posición al bottom-right cuando el widget reaparece desde cero
  useEffect(() => {
    if (prevCountRef.current === 0 && activeExecutions.length > 0) {
      setPos({ bottom: 32, right: 32 });
    }
    prevCountRef.current = activeExecutions.length;
  }, [activeExecutions.length]);

  // Listeners globales de mouse para el drag
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const newLeft = Math.max(0, Math.min(window.innerWidth - 320, dragRef.current.startLeft + dx));
      const newTop = Math.max(0, Math.min(window.innerHeight - 80, dragRef.current.startTop + dy));
      setPos({ top: newTop, left: newLeft });
    };
    const onUp = () => { dragRef.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const handleDragStart = (e) => {
    if (e.button !== 0) return;
    const rect = widgetRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startLeft: rect.left, startTop: rect.top };
    setPos({ top: rect.top, left: rect.left });
    e.preventDefault();
  };

  if (!activeExecutions || activeExecutions.length === 0) return null;

  const hasTerminal = activeExecutions.some(e => isTerminalStatus(e.status));
  const running = activeExecutions.filter(e => { const s = (e.status||'').toLowerCase(); return !isTerminalStatus(e.status) && s !== 'queued' && s !== 'pending'; });
  const queued = activeExecutions.filter(e => { const s = (e.status||'').toLowerCase(); return s === 'queued' || s === 'pending'; });
  const terminal = activeExecutions.filter(e => isTerminalStatus(e.status));
  const sorted = [...running, ...queued, ...terminal];

  const handleView = (exec) => {
    if (isTerminalStatus(exec.status)) { requestViewExecution(exec.id); clearExecution(exec.id); }
    setCurrentView('execute');
  };

  return (
    <div ref={widgetRef} style={{ position: 'fixed', zIndex: 9998, width: 300, ...pos }}>
      <div style={{
        background: 'var(--surface)', borderRadius: '0.875rem',
        border: '1px solid var(--border)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}>
        {/* Header drag */}
        <div
          onMouseDown={handleDragStart}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 14px',
            background: 'var(--primary)', // barra verde 10S
            cursor: 'grab', userSelect: 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PlayCircle size={14} style={{ color: '#fff', opacity: 0.9 }} />
            <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#fff' }}>
              Validaciones activas
            </span>
            <span style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', borderRadius: '1rem', fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px' }}>
              {activeExecutions.length}
            </span>
          </div>
          {hasTerminal && (
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={clearAllTerminal}
              style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.8)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 500 }}
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Lista */}
        <div style={{ overflowY: 'auto', maxHeight: 320 }}>
          {sorted.map(exec => (
            <ExecutionRow key={exec.id} exec={exec} onDismiss={clearExecution} onCancel={cancelExecution} onView={handleView} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN APP ============
const App = () => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <NotificationProvider>
      <LoadingProvider>
        <AuthProvider>
          <ThemeProvider>
            <PermissionsProvider>
              <ExecutionProvider>
                <AppContent />
                <NotificationContainer />
                <LoadingOverlay />
              </ExecutionProvider>
            </PermissionsProvider>
          </ThemeProvider>
        </AuthProvider>
      </LoadingProvider>
    </NotificationProvider>
    </GoogleOAuthProvider>
  );
};

const AppContent = () => {
  const { user, loading } = useAuth();
  const { hideLoading } = useLoading();
  const { theme } = useTheme();
  const [currentView, setCurrentView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') ?? 'dashboard';
  });

  // Detectar ruta pública inicial desde la URL del navegador
  const [publicRoute, setPublicRoute] = useState(() => {
    const path = window.location.pathname;
    if (path === '/register')        return 'register';
    if (path === '/forgot-password') return 'forgot-password';
    if (path === '/accept-invite')   return 'accept-invite';
    if (path === '/billing/success') return 'billing-success';
    if (path === '/auth/sso')        return 'sso';
    return 'login';
  });

  const navigateTo = (route) => {
    const paths = { login: '/', register: '/register', 'forgot-password': '/forgot-password', 'accept-invite': '/accept-invite' };
    window.history.pushState({}, '', paths[route] || '/');
    setPublicRoute(route);
  };

  // Escuchar evento de navegación emitido por el banner
  useEffect(() => {
    const handler = (e) => setCurrentView(e.detail);
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, []);

  // Limpia cualquier overlay de carga huérfano cuando la sesión se pierde
  useEffect(() => {
    if (!user) hideLoading();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Páginas accesibles independientemente del estado de autenticación
  if (publicRoute === 'billing-success') return <BillingSuccessPage />;
  if (publicRoute === 'sso')             return <SsoPage />;

  if (!user) {
    if (publicRoute === 'register')        return <RegisterPage onNavigate={navigateTo} theme={theme} />;
    if (publicRoute === 'forgot-password') return <ForgotPasswordPage onNavigate={navigateTo} />;
    if (publicRoute === 'accept-invite')   return <AcceptInvitePage />;
    return <LoginPage onNavigate={navigateTo} />;
  }

  // Bloquear la app si el superadmin generó una contraseña temporal
  if (user.mustChangePassword) {
    return <ChangePasswordPage />;
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <DashboardContent setCurrentView={setCurrentView} />;
      case 'admin':
        return <SuperAdminModule
          useAuth={useAuth}
          useNotification={useNotification}
          BaseButton={BaseButton}
          BaseInput={BaseInput}
          BaseCard={BaseCard}
        />;
      case 'branding':
        return (
          <BrandingModule 
            useAuth={useAuth}
            usePermissions={usePermissions} 
            useNotification={useNotification}
            useTheme={useTheme}
            BaseButton={BaseButton}
            BaseInput={BaseInput}
            BaseCard={BaseCard}
          />
        );
      case 'templates':
        return <TemplateManagementModule
          useAuth={useAuth}
          usePermissions={usePermissions}
          useNotification={useNotification}
          useLoading={useLoading}
          BaseButton={BaseButton}
          BaseInput={BaseInput}
          BaseCard={BaseCard}
        />;
      case 'validations':
        return <ValidationRulesModule
          useAuth={useAuth}
          usePermissions={usePermissions}
          useNotification={useNotification}
          useLoading={useLoading}
          BaseButton={BaseButton}
          BaseInput={BaseInput}
          BaseCard={BaseCard}
        />;
      case 'execute':
        return <ValidationExecutionModule
          useAuth={useAuth}
          usePermissions={usePermissions}
          useNotification={useNotification}
          useLoading={useLoading}
          useExecution={useExecution}
          BaseButton={BaseButton}
          BaseInput={BaseInput}
          BaseCard={BaseCard}
        />
      case 'compare':
        return <ReportComparisonModule 
          useAuth={useAuth}
          usePermissions={usePermissions}
          useNotification={useNotification}
          BaseButton={BaseButton}
          BaseInput={BaseInput}
          BaseCard={BaseCard}
        />;
      case 'audit':
        return <AuditModule
          useAuth={useAuth}
          usePermissions={usePermissions}
          useNotification={useNotification}
          BaseButton={BaseButton}
          BaseInput={BaseInput}
          BaseCard={BaseCard}
        />;
      case 'users':
        return <UsersModule
          useAuth={useAuth}
          useNotification={useNotification}
          BaseButton={BaseButton}
          BaseInput={BaseInput}
          BaseCard={BaseCard}
        />;
      case 'billing':
        return <BillingModule
          useAuth={useAuth}
          useNotification={useNotification}
          BaseButton={BaseButton}
          BaseCard={BaseCard}
        />;
      case 'profile':
        return <ProfileModule
          useAuth={useAuth}
          useNotification={useNotification}
          BaseButton={BaseButton}
          BaseCard={BaseCard}
        />;
      default:
        return <DashboardContent />;
    }
  };

  return (
    <>
      <MainLayout currentView={currentView} setCurrentView={setCurrentView}>
        {renderCurrentView()}
      </MainLayout>
      <GlobalExecutionWidget setCurrentView={setCurrentView} />
    </>
  );
};

export default App;